import type { Service } from "@/features/services/types/serviceTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { AmountPicker, type AmountPickerProps } from "@/shared/components/ui/AmountPicker";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { cn } from "@/shared/utils/cn";
import { formatMoney } from "@/shared/utils/formatters";

interface AppointmentPricePickerProps extends AmountPickerProps {
  services?: Service[];
}

export function AppointmentPricePicker({ services = [], ...props }: AppointmentPricePickerProps) {
  const suggestedTotal = services.reduce((total, service) => total + (service.basePrice ?? 0), 0);
  const currency = props.currency ?? "USD";

  return (
    <AmountPicker
      {...props}
      renderPreview={(cents, setValue) => {
        if (!services.length) return null;
        const difference = cents / 100 - suggestedTotal;
        return (
          <dl aria-label="Service pricing" className="space-y-0.5 text-sm">
            {services.map((service) => (
              <div key={service.id} className="flex justify-between gap-3 py-0.5">
                <dt className="min-w-0 break-words">{service.name}</dt>
                <dd className="shrink-0 tabular-nums">{formatMoney(service.basePrice ?? 0, currency)}</dd>
              </div>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-1">
              <dt className="flex min-w-0 items-center gap-1 font-bold">
                Suggested total
                <SectionAction size="sm" onClick={() => setValue(Math.round(suggestedTotal * 100).toString())}>Reset</SectionAction>
              </dt>
              <dd className="shrink-0 font-bold tabular-nums">{formatMoney(suggestedTotal, currency)}</dd>
            </div>
            <div className="flex justify-between gap-3 text-xs text-muted-foreground">
              <dt>Difference</dt>
              <dd className={cn("shrink-0 tabular-nums", difference > 0 && "text-primary", difference < 0 && "text-destructive")}>
                {difference > 0 ? "+" : ""}{formatMoney(difference, currency)}
              </dd>
            </div>
          </dl>
        );
      }}
      renderActions={(setValue) => services.length > 0 && (
          <div role="group" aria-label="Discount from suggested total" className="grid grid-cols-3 gap-2">
            {[5, 10, 20].map((discount) => (
              <Button
                key={discount}
                variant="secondary"
                size="touch"
                onClick={() => setValue(Math.round(suggestedTotal * (1 - discount / 100) * 100).toString())}
              >
                -{discount}%
              </Button>
            ))}
        </div>
      )}
    />
  );
}
